/* ==========================================================================
   Category Multi-Select
   PORTAL-77: Tag-style multi-select for assigning categories to a product.
   Selected categories render as removable chips; the input filters the
   dropdown; typing a new name offers an inline "Create" option.
   ========================================================================== */

import { useEffect, useRef, useState } from 'react'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { Check, ChevronDown, Plus, X } from 'lucide-react'
import type { CategoryBrief } from '@/api/types'

/** Sentinel option value used for the inline "Create" row. */
interface CreateSentinel {
  __create: string
}

type OptionValue = string | CreateSentinel

export interface CategoryMultiSelectProps {
  /** All categories available for this pharmacy */
  categories: CategoryBrief[]
  /** Currently selected category ids */
  selectedIds: string[]
  /** Called with the new full selection */
  onChange: (ids: string[]) => void
  /** Creates a category and returns it (or null on failure) */
  onCreateCategory?: (name: string) => Promise<CategoryBrief | null>
  /** id for the combobox input, so a FormField label can reference it */
  inputId?: string
  disabled?: boolean
}

export function CategoryMultiSelect({
  categories,
  selectedIds,
  onChange,
  onCreateCategory,
  inputId,
  disabled = false,
}: CategoryMultiSelectProps) {
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)

  // The create flow awaits a network call; read the selection through a ref
  // so chips removed/added meanwhile aren't clobbered by a stale closure.
  const selectedIdsRef = useRef(selectedIds)
  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  const selected = categories.filter((c) => selectedIds.includes(c.id))
  const trimmedQuery = query.trim()

  const filtered = trimmedQuery
    ? categories.filter((c) =>
        c.name.toLowerCase().includes(trimmedQuery.toLowerCase())
      )
    : categories

  const exactMatch = categories.some(
    (c) => c.name.toLowerCase() === trimmedQuery.toLowerCase()
  )
  const canCreate = Boolean(onCreateCategory && trimmedQuery && !exactMatch)

  async function createAndSelect(name: string) {
    if (!onCreateCategory || creating) return
    setCreating(true)
    try {
      const created = await onCreateCategory(name)
      if (created) {
        const current = selectedIdsRef.current
        onChange(
          current.includes(created.id) ? current : [...current, created.id]
        )
        setQuery('')
      }
    } finally {
      setCreating(false)
    }
  }

  function handleChange(values: OptionValue[]) {
    const createEntry = values.find(
      (v): v is CreateSentinel => typeof v !== 'string'
    )
    if (createEntry) {
      void createAndSelect(createEntry.__create)
      return
    }
    onChange(values as string[])
    setQuery('')
  }

  function removeChip(id: string) {
    onChange(selectedIds.filter((selectedId) => selectedId !== id))
  }

  return (
    <Combobox
      multiple
      immediate
      value={selectedIds as OptionValue[]}
      onChange={handleChange}
      disabled={disabled || creating}
    >
      <div className="relative">
        <div className="flex flex-wrap items-center gap-1.5 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 focus-within:border-deep-purple focus-within:ring-2 focus-within:ring-lavender transition-colors">
          {selected.map((category) => (
            <span
              key={category.id}
              className="inline-flex items-center gap-1 bg-soft-lavender text-deep-purple text-sm px-2 py-0.5 rounded"
            >
              {category.name}
              <button
                type="button"
                onClick={() => removeChip(category.id)}
                disabled={disabled || creating}
                className="hover:text-charcoal transition-colors"
                aria-label={`Remove ${category.name}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <ComboboxInput
            id={inputId}
            className="flex-1 min-w-[8rem] border-0 p-1 text-sm text-charcoal placeholder-gray-400 focus:outline-none focus:ring-0"
            placeholder={
              selected.length > 0 ? '' : 'Search or create categories...'
            }
            onChange={(e) => setQuery(e.target.value)}
          />
          <ComboboxButton
            className="p-1 text-gray-400 hover:text-gray-600"
            aria-label="Show categories"
          >
            <ChevronDown className="w-4 h-4" />
          </ComboboxButton>
        </div>

        <ComboboxOptions className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black/5 max-h-60 overflow-auto py-1 focus:outline-none">
          {filtered.map((category) => (
            <ComboboxOption
              key={category.id}
              value={category.id}
              className="flex items-center justify-between px-3 py-2 text-sm text-charcoal cursor-pointer data-[focus]:bg-gray-100"
            >
              <span>{category.name}</span>
              {selectedIds.includes(category.id) && (
                <Check className="w-4 h-4 text-deep-purple" />
              )}
            </ComboboxOption>
          ))}

          {canCreate && (
            <ComboboxOption
              value={{ __create: trimmedQuery }}
              className="flex items-center px-3 py-2 text-sm text-deep-purple cursor-pointer data-[focus]:bg-gray-100"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {creating ? 'Creating...' : `Create "${trimmedQuery}"`}
            </ComboboxOption>
          )}

          {filtered.length === 0 && !canCreate && (
            <div className="px-3 py-2 text-sm text-gray-500">
              {categories.length === 0
                ? 'No categories yet — type a name to create one.'
                : 'No categories match your search.'}
            </div>
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  )
}

export default CategoryMultiSelect
